import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  DangerActionButton,
  Modal,
  PrimaryActionButton,
  SecondaryActionButton,
  StonePanel,
} from '@aztec-realms/ui';
import {
  exportPrivacyCollection,
  fetchPrivacyStatus,
  requestAccountErasure,
  type PrivacyCollection,
  type PrivacyStatus,
} from './data/player-privacy';
import { invalidateBrowserSession } from './data/browser-api';
import { terminateBrowserSession } from './data/session-teardown';
import './privacy-settings.css';

export function PrivacySettings({
  online,
  interactionLocked = false,
}: {
  online: boolean;
  interactionLocked?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<PrivacyStatus>();
  const [password, setPassword] = useState('');
  const [collection, setCollection] = useState<PrivacyCollection>('identity');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const operation = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const available = online && !interactionLocked;
  const active = open && available;
  const [previousActive, setPreviousActive] = useState(active);
  if (previousActive !== active) {
    setPreviousActive(active);
    if (!active) {
      setPassword('');
      setBusy(false);
      setStatus(undefined);
    }
  }
  useEffect(() => {
    if (!open || !available) return;
    const controller = new AbortController();
    void fetchPrivacyStatus(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setStatus(next);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setMessage('Privacy options are unavailable. Close this panel and try again.');
      });
    return () => controller.abort();
  }, [open, available]);
  useEffect(() => {
    if (!open || !available) {
      operation.current?.abort();
    }
    return () => operation.current?.abort();
  }, [open, available]);

  async function act(kind: 'export' | 'delete') {
    if (operation.current || !available || !password || !status || (kind === 'delete' && !confirm))
      return;
    const controller = new AbortController();
    operation.current = controller;
    setBusy(true);
    setMessage('');
    try {
      if (kind === 'export') {
        const result = await exportPrivacyCollection(collection, password, controller.signal);
        controller.signal.throwIfAborted();
        const url = URL.createObjectURL(
          new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' }),
        );
        const link = document.createElement('a');
        link.href = url;
        link.download = `aztec-realms-${collection}.json`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        setMessage('Your download is ready. Keep it private; choose another category if needed.');
      } else {
        const receipt = await requestAccountErasure(password, controller.signal);
        setPassword('');
        navigate('/login', { replace: true, state: { privacyRequest: receipt.request_id } });
        invalidateBrowserSession({
          code: 'signed_out',
          message: 'Account closed; removal request recorded',
        });
        await terminateBrowserSession(queryClient, { online: false });
      }
    } catch (error) {
      if (!controller.signal.aborted)
        setMessage(error instanceof Error ? error.message : 'The request could not be confirmed.');
    } finally {
      if (operation.current === controller) operation.current = null;
      setPassword('');
      setBusy(false);
    }
  }

  return (
    <>
      <StonePanel className="settings-card">
        <h2>Your data</h2>
        <p>Download your information or request account removal.</p>
        <SecondaryActionButton
          disabled={!available}
          onClick={() => {
            setOpen(true);
            setConfirm(false);
            setMessage('');
          }}
        >
          Manage my data
        </SecondaryActionButton>
        {!available && (
          <p>
            {online ? 'Available when you have stopped safely.' : 'Connect to manage your data.'}
          </p>
        )}
      </StonePanel>
      {open &&
        createPortal(
          <div className="privacy-overlay">
            <Modal open title="Your data" onClose={() => setOpen(false)}>
              <div className="privacy-panel">
                {!available ? (
                  <p>Connect and stop safely to continue.</p>
                ) : !status ? (
                  <p>Loading your privacy options…</p>
                ) : (
                  <>
                    <h3>Download a copy</h3>
                    <p>{status.export_scope_notice}</p>
                    <label>
                      Data category
                      <select
                        value={collection}
                        disabled={busy}
                        onChange={(event) => setCollection(event.target.value as PrivacyCollection)}
                      >
                        {status.export_collections.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Current password
                      <input
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        disabled={busy}
                        onChange={(event) => setPassword(event.target.value)}
                      />
                    </label>
                    <PrimaryActionButton
                      disabled={busy || !password}
                      onClick={() => void act('export')}
                    >
                      {busy ? 'Please wait…' : 'Download selected category'}
                    </PrimaryActionButton>
                    <details>
                      <summary>What is not included?</summary>
                      <p>
                        Other players’ private information and protected security records are not
                        included.
                      </p>
                      <ul>
                        {status.restricted_categories.map((item) => (
                          <li key={item}>{item.replaceAll('_', ' ')}</li>
                        ))}
                      </ul>
                    </details>
                    <hr />
                    <h3>Close account and request removal</h3>
                    <p>{status.erasure_notice}</p>
                    <p>
                      This closes your account immediately and signs out all devices. Financial,
                      security and moderation records may require review or continued retention. It
                      does not confirm that all data has been erased.
                    </p>
                    <label className="privacy-confirm">
                      <input
                        type="checkbox"
                        checked={confirm}
                        disabled={busy}
                        onChange={(event) => setConfirm(event.target.checked)}
                      />
                      I understand my account will close and removal needs review.
                    </label>
                    <DangerActionButton
                      disabled={busy || !password || !confirm}
                      onClick={() => void act('delete')}
                    >
                      Close account and request removal
                    </DangerActionButton>
                  </>
                )}
                {message && <p role="status">{message}</p>}
              </div>
            </Modal>
          </div>,
          document.querySelector('.ar-game-shell') ?? document.body,
        )}
    </>
  );
}
